import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import * as Sentry from '@sentry/react';

import useLanguage from '../../../contexts/language/LanguageContext';
import { Page, TransitionUrl, TranslationPath } from '../../../contexts/kioskConfiguration/type';
import { RootState } from '../../../store/store';
import { registerTransportation } from '../../../apollo/mutations/registerTransportatioin';
import { isLoadingType, isUnloadingType } from '../../../utils/transportationType';
import { Loader } from '../../../components/loader/Loader';
import { Box, Flex } from '@radix-ui/themes';
import { getTranslationByPath } from '../../../utils/translations';
import { Footer } from '../../../layout/footer/Footer';
import { MainContent } from '../../../layout/content/MainContent';
import { getTransportation } from '../../../apollo/queries/getTransportation';
import { setFetchedTransportation } from '../../../store/slices/transportation/transportationSlice';
import {
  getPushNotificationSubscription,
  isPushNotificationSupported
} from '../../../utils/notification';
import { subscribeToTransportationPushNotification } from '../../../apollo/mutations/subscribeToTransportation';
import { setSelectedPage } from '../../../store/slices/app/appSlice';


export type RegistrationTemplateType = {
  transitionUrl: TransitionUrl
  registrationSuccessText: TranslationPath
  registrationErrorText: TranslationPath
  infoText: TranslationPath
  backToStartText: TranslationPath
  checkImage: string
  mobileImage: string
}

type Props = {
  page: Page<RegistrationTemplateType>
}


export const RegistrationTemplate = (props: Props): React.ReactElement => {
  const fetchedTransportation = useSelector((state: RootState) => state.transportation.fetchedTransportation)
  const transportationTypeId = useSelector((state: RootState) => state.transportation.transportationTypeId)
  const transportationForm = useSelector((state: RootState) => state.transportation.transportationForm)

  const language = useLanguage();
  const dispatch = useDispatch()

  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [isRegistrationError, setRegistrationError] = useState<boolean>(false)

  const subscribeToPushNotifications = async (transportationId: number) => {
    try {
      if (isPushNotificationSupported()) {
        const subscription = await getPushNotificationSubscription()
        if (subscription) {
          await subscribeToTransportationPushNotification({
            transportationId,
            subscription,
            qtirLanguageId: language.qtirLanguageId,
            eventType: 'CALL_TRANSPORTATION',
          })
        }
      }
    } catch (e) {
      console.log(e)
      Sentry.captureException(e, { tags: { type: 'subscribeToPushNotifications' } })
    }
  }

  useEffect(() => {
    const init = async () => {
      let data;
      if (fetchedTransportation === null) {
        if (!transportationTypeId) {
          return
        }

        data = {
          registrationNumber: transportationForm.registrationNumber,
          authCode: transportationForm.authCode,
          phoneNumber: transportationForm.phoneNumber,
          driverName: transportationForm.driverName,
          transportationTypeId: transportationTypeId,
          qtirLanguageId: language.qtirLanguageId,
          carCompanyId: isUnloadingType(transportationTypeId) ? transportationForm.carCompanyId : undefined,
          carQuantity: isUnloadingType(transportationTypeId) ? parseInt(transportationForm.carQuantity, 10) : undefined,
          destinationId: isUnloadingType(transportationTypeId) ? transportationForm.destinationId : undefined,
          driverEmail: transportationForm.email,
          voyage: isLoadingType(transportationTypeId) ? transportationForm.voyage : undefined,
        }
      } else {
        data = {
          qtirLanguageId: language.qtirLanguageId,
          authCode: transportationForm.authCode,
          transportationId: fetchedTransportation.id,
        }
      }

      try {
        const result = await registerTransportation(data)
        setRegistrationError(!result.data?.registerTransportation.transportationId)
        if (result.data?.registerTransportation.transportationId) {
          const findTransportationResult = await getTransportation({
            id: result.data?.registerTransportation.transportationId,
            qtirLanguageId: language.qtirLanguageId,
          })

          const foundTransportation = findTransportationResult.data.getTransportation.transportation
          dispatch(setFetchedTransportation(foundTransportation))
          subscribeToPushNotifications(foundTransportation.id)
        }
      } catch (e) {
        console.log(e)
        setRegistrationError(true)
        Sentry.captureException(e, { tags: { type: 'registration', data: JSON.stringify(data) } })
      }
      setIsLoading(false)
    }
    init()
  }, []);

  if (isLoading) {
    return <Loader />
  }

  return (
    <>
      <MainContent>
        <Flex direction="column" gap="1" width="100%" className="items-center">
          <Box maxWidth="500px" className="w-full flex flex-col items-center text-center">
            {!isRegistrationError && (
              <>
                <img src={`${process.env.APP_DATA_LINK + props.page.data.checkImage}`} className="w-16 h-16 mb-5" alt=""/>

                <div dangerouslySetInnerHTML={{ __html: getTranslationByPath(language.translations, props.page.data.registrationSuccessText) }} />

                <img src={`${process.env.APP_DATA_LINK + props.page.data.mobileImage}`} className="w-16 h-16 mb-5 mt-10" alt=""/>

                <div dangerouslySetInnerHTML={{ __html: getTranslationByPath(language.translations, props.page.data.infoText) }} />
              </>
            )}
            {isRegistrationError && (
              <p>
                {getTranslationByPath(language.translations, props.page.data.registrationErrorText)}
              </p>
            )}
          </Box>
        </Flex>
      </MainContent>

      <Footer
        backUrl={isRegistrationError ? props.page.footer?.back : undefined}
        onContinue={
          isRegistrationError
            ? undefined
            : () => { dispatch(setSelectedPage(props.page.data.transitionUrl)) }
        } />
    </>
  )
}
